/**
 * `WAFBlockBanner` — ADR-0207 Slice 2. Amber warning surfaced when the
 * readiness pipeline detects a WAF is returning 403 to the anonymous
 * scanner (`signals.crawler_access === 'blocked'`).
 *
 * A WAF that blocks AI crawlers makes the agent card, MCP server, and
 * llms.txt appear absent to ChatGPT / Claude / Perplexity even when
 * they are actually deployed — so the merchant needs to bypass Sill's
 * three paths in their firewall, not "install Sill again". The copy is
 * fixed and shared byte-for-byte with the readiness-report email
 * (`services/api/src/email/templates/readiness-report.ts`) so a
 * merchant reading either surface sees the same fix.
 *
 * The banner is additive — a failed / rate-limited readiness call
 * silently renders nothing. Never blocks the surface it sits above.
 */

import { AlertTriangle } from 'lucide-react';

import { WAF_BYPASS_PATHS } from '@sill/types';

import { useC } from '../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

// Re-export the shared list so the dashboard-local consumer imports
// cost one line, not an extra `@sill/types` round-trip. Single source
// of truth is `packages/types/src/waf-bypass-paths.ts`.
export { WAF_BYPASS_PATHS };

export function WAFBlockBanner(): JSX.Element {
  const C = useC();
  return (
    <div
      className="p-4 mb-4"
      style={{
        background: C.amberDim,
        // Thin 1px full border for keyline; emphasised LEFT border in the
        // Sill escalate color per ADR-0207 §Slices Slice-2.
        border: `1px solid ${C.border}`,
        borderLeft: `3px solid ${C.amberFg}`,
        borderRadius: 2,
      }}
      role="alert"
      data-testid="waf-block-banner"
    >
      <div className="flex items-start gap-2.5">
        <AlertTriangle
          size={16}
          color={C.amberFg}
          strokeWidth={2}
          style={{ marginTop: 2, flexShrink: 0 }}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              fontWeight: 600,
              color: C.text,
              margin: 0,
              lineHeight: 1.4,
            }}
          >
            WAF is blocking AI agent crawlers
          </p>
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 12.5,
              color: C.body,
              margin: '6px 0 0',
              lineHeight: 1.55,
            }}
          >
            Your firewall returned 403 to the scanner. AI agents (ChatGPT,
            Claude, Perplexity) will see your agent card, MCP server, and
            llms.txt as missing even though they may be deployed.
          </p>
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              margin: '10px 0 4px',
              lineHeight: 1.4,
            }}
          >
            Add a WAF bypass rule for:
          </p>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
            }}
          >
            {WAF_BYPASS_PATHS.map((path) => (
              <li key={path}>
                <code
                  style={{
                    fontFamily: monoFont,
                    fontSize: 11,
                    color: C.text,
                    background: C.surface2,
                    border: `1px solid ${C.borderSoft}`,
                    borderRadius: 2,
                    padding: '2px 6px',
                    letterSpacing: 0.2,
                  }}
                >
                  {path}
                </code>
              </li>
            ))}
          </ul>
          <p
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              margin: '10px 0 0',
              lineHeight: 1.5,
            }}
          >
            In Cloudflare: Security &rarr; WAF &rarr; Custom Rules &rarr;
            Skip rule for these paths.
          </p>
        </div>
      </div>
    </div>
  );
}
